"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.collectInfo = void 0;
const utils_1 = require("./utils");
const mraid_commons_1 = require("./mraid-commons");
const session_1 = require("./session");
const gtx_id_1 = require("./gtx-id");
const viewability_1 = require("./viewability");
const pipe_1 = require("./pipe");
function getLocationInfo() {
    if (typeof window !== 'undefined') {
        let windowObject = window;
        let currentLocInfo = undefined;
        while (windowObject) {
            try {
                currentLocInfo = {
                    location: windowObject.location.href,
                    referrer: windowObject.document.referrer
                };
            }
            catch (ignored) {
                return currentLocInfo;
            }
            windowObject = windowObject !== window.top ? windowObject.parent : undefined;
        }
        return currentLocInfo;
    }
    else {
        return undefined;
    }
}
function getScreenSize() {
    if (typeof window !== 'undefined') {
        return {
            width: window.screen.width,
            height: window.screen.height
        };
    }
    else {
        return undefined;
    }
}
function updateTagFrequency(slotTag) {
    if (typeof window !== 'undefined' && slotTag != null && slotTag !== '') {
        const tagFreqKey = `${slotTag}_freq`;
        const tagFreq = window.sessionStorage.getItem(tagFreqKey);
        const nextTagFreq = tagFreq != null ? String(parseInt(tagFreq, 10) + 1) : '1';
        window.sessionStorage.setItem(tagFreqKey, nextTagFreq);
        return nextTagFreq;
    }
    else {
        return '1';
    }
}
function collectLocationInfo(properties) {
    const locationInfo = getLocationInfo();
    if (locationInfo) {
        return {
            ...properties,
            _js_site_page: locationInfo.location,
            _js_site_ref: locationInfo.referrer
        };
    }
    else {
        return properties;
    }
}
function collectScreenSize(properties) {
    const screenInfo = getScreenSize();
    if (screenInfo) {
        return {
            ...properties,
            _js_device_w: String(screenInfo.width),
            _js_device_h: String(screenInfo.height)
        };
    }
    return properties;
}
function collectTagFrequency(tag) {
    return (properties) => {
        let tagFreq = '1';
        try {
            const updatedFreq = updateTagFrequency(tag);
            tagFreq = updatedFreq; /*parseInt(updatedFreq, 10) > 200 ? '200+' : updatedFreq*/
        }
        catch (ignored) { }
        return { ...properties, _js_tag_freq: tagFreq };
    };
}
function collectGdpr(properties) {
    const gdpr = properties.gdpr;
    if (gdpr != null) {
        if (String(gdpr) === '1') {
            return { ...properties, gdpr: 'true' };
        }
        else if (String(gdpr) === '0') {
            return { ...properties, gdpr: 'false' };
        }
    }
    return properties;
}
function collectViewability(properties) {
    const viewableTechnology = (0, viewability_1.getViewableTechnology)();
    const measurable = viewableTechnology === viewability_1.PixelType.None ? '0' : '1';
    return {
        ...properties,
        _js_vis_type: String(viewableTechnology),
        _js_measurable: measurable
    };
}
function collectAdaptiveSizes(adaptiveSizes) {
    return (properties) => {
        if (adaptiveSizes) {
            const windowObject = (0, utils_1.getTopAccessibleWindow)();
            if (windowObject) {
                const width = windowObject.innerWidth;
                const height = windowObject.innerHeight;
                const adaptiveSize = adaptiveSizes
                    .map((size) => size.split('x').map((dimension) => parseInt(dimension, 10)))
                    .reduce((acc, size) => (width >= size[0] && height >= size[1] ? acc.concat([size]) : acc), [])
                    .sort((a, b) => b[0] * b[1] - a[0] * a[1])
                    .reduce((acc, size) => (acc === '' ? size.join('x') : acc), '');
                if (adaptiveSize) {
                    return {
                        ...properties,
                        _imp_banner_size: adaptiveSize
                    };
                }
            }
        }
        return properties;
    };
}
function collectCustomProperties(tagProperties) {
    return Object.keys(tagProperties).reduce((acc, key) => {
        if (key !== '_tag' && key !== '_opts' && key !== 'b64') {
            acc[key] = tagProperties[key];
        }
        return acc;
    }, {});
}
function collectB64Properties(b64) {
    return (properties) => {
        if (b64) {
            const decodedB64 = (0, utils_1.decodeB64)(b64);
            if (decodedB64) {
                return {
                    ...properties,
                    ...Object.keys(decodedB64).reduce((acc, key) => {
                        if (key !== 'tag_ext') {
                            acc[key] = decodedB64[key];
                        }
                        return acc;
                    }, {})
                };
            }
        }
        return properties;
    };
}
function collectMraidInfo(properties) {
    let p = properties;
    const mraidEnv = typeof window !== 'undefined' ? (0, mraid_commons_1.findMraidEnvObject)(window) : undefined;
    const mraidWindow = typeof window !== 'undefined' ? (0, mraid_commons_1.findMraidWindow)(window) : undefined;
    if (mraidEnv || mraidWindow) {
        p = { ...p, _js_mraid: '1' };
    }
    if (mraidEnv && mraidEnv.sdk) {
        p = { ...p, _js_sdk: mraidEnv.sdk };
    }
    if (mraidEnv && mraidEnv.ifa) {
        p = { ...p, _js_ifa: mraidEnv.ifa };
    }
    if (mraidEnv && mraidEnv.appId) {
        p = { ...p, _js_app_id: mraidEnv.appId };
    }
    return p;
}
function collectVersionInfo(properties) {
    return {
        ...properties,
        _js_imp_tsver: '5.0.0',
        _js_tstamp: String(new Date().getTime())
    };
}
function collectAmpInfo(properties) {
    let p = properties;
    if (typeof window !== 'undefined' && window.context) {
        const sourceUrl = window.context.sourceUrl;
        const referrer = window.context.referrer;
        if (sourceUrl || sourceUrl === '') {
            p = { ...p, _js_site_page: sourceUrl };
        }
        if (referrer || referrer === '') {
            p = { ...p, _js_site_ref: referrer };
        }
    }
    return p;
}
function collectOptions(opts) {
    return (properties) => {
        let p = properties;
        if (opts) {
            const inApp = opts.in_app;
            if (inApp != null && inApp !== '') {
                p = { ...p, in_app: inApp };
            }
            if (opts.autoscale != null) {
                p = { ...p, _js_autoscale: String(opts.autoscale) };
            }
            if (!!opts.autohide) {
                p = { ...p, _js_autohide: 'true' };
            }
            if (opts.delivery_method) {
                p = { ...p, _js_imp_banner_creative_attr: opts.delivery_method };
            }
        }
        return p;
    };
}
function collectAncestorOrigins(properties) {
    const ancestorOrigins = typeof window !== 'undefined' ? window.location.ancestorOrigins : undefined;
    if (ancestorOrigins) {
        return {
            ...properties,
            _js_ao: Array.prototype.slice.call(ancestorOrigins).join(',')
        };
    }
    return properties;
}
function collectExternalApiInfo(properties) {
    if (typeof window !== 'undefined') {
        return {
            ...properties,
            _js_sf: window['sfAPI'] || window['$sf'] ? '1' : '0',
            _js_fif: window['inDapIF'] === true ? '1' : '0',
            js_imp_banner_topframe: window !== window.top ? '1' : '0'
        };
    }
    return properties;
}
function collectSessionData(noSession) {
    return (properties) => {
        return noSession !== true ? { ...properties, ...(0, session_1.getCurrentSessionData)() } : properties;
    };
}
function collectGtxId(properties) {
    return { ...properties, _js_gtx_id: (0, gtx_id_1.getGtxId)() };
}
function collectInfo(tagProperties) {
    const { _opts, _tag } = tagProperties;
    const b64 = (_opts === null || _opts === void 0 ? void 0 : _opts.b64) || tagProperties.b64;
    const adaptiveSizes = _opts === null || _opts === void 0 ? void 0 : _opts.adaptive_sizes;
    const noSession = _opts === null || _opts === void 0 ? void 0 : _opts.no_session;
    return (0, pipe_1.pipe)(tagProperties, collectCustomProperties, collectB64Properties(b64), collectLocationInfo, collectScreenSize, collectTagFrequency(_tag), collectGdpr, collectViewability, collectAdaptiveSizes(adaptiveSizes), collectSessionData(noSession), collectExternalApiInfo, collectAncestorOrigins, collectOptions(_opts), collectMraidInfo, collectVersionInfo, collectAmpInfo, collectGtxId);
}
exports.collectInfo = collectInfo;
//# sourceMappingURL=info.js.map